/**
 * How old the displayed rates actually are.
 *
 * This replaces the countdown ring that was originally specified. A ring
 * draining toward the next refresh implies the refresh will bring new numbers,
 * but the underlying data is daily central-bank reference data — most refreshes
 * return exactly what was already on screen. Counting down to a non-event is a
 * visual claim the data does not support, and the product brief's honesty rule
 * exists to prevent precisely that.
 *
 * What this measures instead is a fact: the age of the oldest rate on screen.
 * The arc FILLS as the data ages rather than draining toward an event, so it
 * never implies something is about to happen.
 */

export type AgeState = "fresh" | "stale" | "offline";

export interface DataAge {
  /** Whole hours since the oldest quote date began (UTC). */
  readonly hours: number;
  /** Whole calendar days between that date and today, UTC. */
  readonly days: number;
  /** 0-1 across a 24-hour window, for the arc. Clamped at 1. */
  readonly fraction: number;
  readonly state: AgeState;
  /** Null when no rate date is known at all. */
  readonly date: string | null;
}

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/** Midnight UTC of a YYYY-MM-DD string, which is when the day's rate applies. */
function startOfDayUtc(isoDate: string): number {
  return Date.parse(`${isoDate}T00:00:00Z`);
}

function startOfTodayUtc(now: Date): number {
  return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

export function dataAge(
  oldestIsoDate: string | null,
  now: Date,
  offline = false,
): DataAge {
  if (oldestIsoDate === null) {
    return {
      hours: 0,
      days: 0,
      fraction: 1,
      state: offline ? "offline" : "stale",
      date: null,
    };
  }

  const quoteStart = startOfDayUtc(oldestIsoDate);
  if (Number.isNaN(quoteStart)) {
    return { hours: 0, days: 0, fraction: 1, state: "stale", date: oldestIsoDate };
  }

  const elapsed = Math.max(0, now.getTime() - quoteStart);
  const hours = Math.floor(elapsed / HOUR_MS);
  const days = Math.max(0, Math.round((startOfTodayUtc(now) - quoteStart) / DAY_MS));
  const fraction = Math.min(elapsed / DAY_MS, 1);

  // Offline outranks everything: cached numbers of any age deserve the warning.
  const state: AgeState = offline ? "offline" : hours >= 24 ? "stale" : "fresh";

  return { hours, days, fraction, state, date: oldestIsoDate };
}

/**
 * Short bilingual phrase for the age, for the cases that need no numeral.
 *
 * Digits are never produced here — the caller routes any number through
 * formatNumber, so the Latin-digit invariant lives in exactly one place.
 *
 * Arabic has a dual, so "2 days" is a word ("يومين"), not a count. Rendering
 * it as "منذ 2 أيام" is the kind of mistake that marks an interface as
 * translated rather than written.
 */
export function ageWord(days: number, locale: "en" | "ar"): string | null {
  const ar = locale === "ar";
  if (days <= 0) return ar ? "اليوم" : "today";
  if (days === 1) return ar ? "أمس" : "yesterday";
  if (days === 2 && ar) return "منذ يومين";
  return null; // caller formats "N days" with a real number
}

/**
 * Arabic counted-noun agreement for days:
 *   3-10  take the plural  -> "منذ 5 أيام"
 *   11+   take the singular accusative -> "منذ 15 يومًا"
 */
export function arabicDayNoun(days: number): string {
  return days >= 3 && days <= 10 ? "أيام" : "يومًا";
}
